import { useCallback, useEffect, useState } from 'react'
import { ColumnsType } from 'antd/lib/table'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import dayjs from 'dayjs'
import { debounceTime, distinctUntilChanged, fromEvent, map, of, switchMap, tap } from 'rxjs'
import { Badge } from 'antd'
import { saveAs } from 'file-saver'
import { SorterResult } from 'antd/es/table/interface'
import { ActionBlock, Box, ButtonIcon, EnText } from './styles'
import { IMAGE_URL } from '@/constant/IMAGE'
import sideBarInformationAtom from '@/recoil/sideBarInformation'
import WellBeingPartnerSidebarInfo from '@/components/feature/WellBeingPartnerPage/WellBeingPartnerSidebarInfo'
import { useLazyQuery, useMutation } from '@apollo/client'
import { activeHealthPartnerBranchGql, activeHealthPartnerGql, addressGql } from '@/gql'
import { SortByEnum } from '@/constant/SORT_BY'
import {
  ActiveHealthPartnerBranchWithScanCount,
  BackofficeGetActiveHealthPartnerBranchPaginationWithScanCountInputInterface,
} from '@/gql/activeHealthPartner/backofficeGetActiveHealthPartnerBranchPaginationWithScanCount/interface'
import message from '@/libs/message'
import { SORTER, SorterEnum } from '@/constant/USER'
import userAtom from '@/recoil/user'
import { validatePerm } from '@/libs/validatePermission'
import { BACKOFFICE_USER_PERMISSION } from '@/constant/BACKOFFICE_PERMISSION'
import ConfirmModalHook from '@/components/common/ConfirmModal/ConfirmModal.hook'
import { WellBeingModalActionEnum, WellBeingModeEnum } from '@/constant/WELL_BEING'

const WellBeingBranchTableHook = ({
  partnerId,
  displayMode,
}: {
  partnerId: string
  displayMode: WellBeingModeEnum
}) => {
  const isEditMode = displayMode === WellBeingModeEnum.EDIT

  const user = useRecoilValue(userAtom)
  const isAddBranch =
    isEditMode && validatePerm([BACKOFFICE_USER_PERMISSION.WBE010102], user.permissions)
  const searchElement = document.getElementById('well-being-branch-search')
  const { setConfirmModal } = ConfirmModalHook()
  const [isTyping, setIsTyping] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const [sort, setSort] = useState<[string, SortByEnum | '']>(['createdAt', SortByEnum.DESC])
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [modalAction, setModalAction] = useState<WellBeingModalActionEnum>(
    WellBeingModalActionEnum.CREATE,
  )
  const [selectedBranch, setSelectedBranch] = useState<
    ActiveHealthPartnerBranchWithScanCount | undefined
  >()
  const [pagination, setPagination] = useState<{
    page: number
    limit: number
    totalItems?: number
  }>({
    page: 1,
    limit: 10,
    totalItems: 0,
  })
  const [
    backofficeGetActiveHealthPartnerBranchPaginationWithScanCount,
    {
      data: activeHealthPartnerBranchData,
      loading: activeHealthPartnerBranchLoading,
      refetch: refetchPagination,
    },
  ] = useLazyQuery(
    activeHealthPartnerGql.query.backofficeGetActiveHealthPartnerBranchPaginationWithScanCount,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        if (data.backofficeGetActiveHealthPartnerBranchPaginationWithScanCount.meta) {
          setPagination({
            page: data.backofficeGetActiveHealthPartnerBranchPaginationWithScanCount.meta
              .currentPage,
            limit:
              data.backofficeGetActiveHealthPartnerBranchPaginationWithScanCount.meta.itemsPerPage,
            totalItems:
              data.backofficeGetActiveHealthPartnerBranchPaginationWithScanCount.meta.totalItems,
          })
        }
      },
    },
  )
  const [backofficeGetQrByActiveHealthPartnerBranchId] = useLazyQuery(
    activeHealthPartnerGql.query.backofficeGetQrByActiveHealthPartnerBranchId,
  )
  const [getAddress] = useLazyQuery(addressGql.query.backofficeGetAddresses, {
    fetchPolicy: 'no-cache',
  })
  const setSidebarInformation = useSetRecoilState(sideBarInformationAtom)
  const [deleteBranchMutation] = useMutation(
    activeHealthPartnerBranchGql.mutation.backofficeRemoveActiveHealthPartnerBranch,
    {
      fetchPolicy: 'no-cache',
    },
  )

  const onModalBranchDelete = (data: ActiveHealthPartnerBranchWithScanCount) => {
    if (!data) return

    setConfirmModal({
      isVisible: true,
      onOkFunction: () => {
        deleteBranchMutation({
          variables: {
            activeHealthPartnerBranchId: data.activeHealthPartnerBranch.id,
          },
        })
          .then(() => {
            refetchPagination()
            message.success({
              content: 'ลบสาขาสำเร็จ',
              duration: 10,
            })
          })
          .catch((error) => {
            const errorMessage = error?.message
              ? `ลบสาขาไม่สำเร็จ (${error.message})`
              : 'ลบสาขาไม่สำเร็จ'
            message.error({
              content: errorMessage,
              duration: 10,
            })
          })
          .finally(() => {
            setConfirmModal({
              isVisible: false,
              onOkFunction: Function,
              dataTestId: undefined,
            })
          })
      },
      title: 'คุณแน่ใจที่จะลบสาขาหรือไม่',
      bodyText: `ลบสาขา "${data.activeHealthPartnerBranch.displayNameTh} (${data.activeHealthPartnerBranch.displayNameEn})"`,
      okText: 'ลบ',
      okButtonProps: { type: 'primary', danger: true },
      dataTestId: 'well-being-branch-modal-delete',
    })
  }

  const columns: ColumnsType<ActiveHealthPartnerBranchWithScanCount | {}> = [
    {
      title: 'ลำดับ',
      key: 'index',
      dataIndex: 'index',
      align: 'center',
      render: (_, __, index) => (pagination.page - 1) * pagination.limit + index + 1,
    },
    {
      width: '15%',
      title: 'ชื่อสาขา',
      render: (_, data) => {
        const d = data as ActiveHealthPartnerBranchWithScanCount
        return (
          <>
            <Box>{d.activeHealthPartnerBranch.displayNameTh}</Box>
            <EnText>{d.activeHealthPartnerBranch.displayNameEn}</EnText>
          </>
        )
      },
    },
    {
      title: 'วันที่เริ่มต้น',
      dataIndex: ['activeHealthPartnerBranch', 'startDate'],
      key: 'startDate',
      sorter: true,
      sortDirections: [SorterEnum.DESC, SorterEnum.ASC],
      defaultSortOrder: null,
      render: (startDate) => {
        return (startDate && dayjs(startDate).format('DD-MM-YYYY')) || '-'
      },
    },
    {
      title: 'วันที่สิ้นสุด',
      dataIndex: ['activeHealthPartnerBranch', 'endDate'],
      key: 'endDate',
      sorter: true,
      sortDirections: [SorterEnum.DESC, SorterEnum.ASC],
      defaultSortOrder: null,
      render: (endDate) => {
        return (endDate && dayjs(endDate).format('DD-MM-YYYY')) || '-'
      },
    },
    {
      title: 'สถานะ QR CODE',
      dataIndex: ['activeHealthPartnerBranch', 'qr', 'isActive'],
      key: 'qr.isActive',
      sorter: true,
      sortDirections: [SorterEnum.DESC, SorterEnum.ASC],
      defaultSortOrder: null,
      render: (isActive) => {
        return (
          <>
            <Badge status={isActive ? 'success' : 'error'} />{' '}
            {isActive ? 'เปิดการใช้งาน' : 'ปิดการใช้งาน'}
          </>
        )
      },
    },
    {
      title: 'สร้างโดย',
      render: (_, data) => {
        const d = data as ActiveHealthPartnerBranchWithScanCount

        return (
          (d.activeHealthPartnerBranch.createdBy.firstname &&
            d.activeHealthPartnerBranch.createdBy.lastname &&
            `${d.activeHealthPartnerBranch.createdBy.firstname} ${d.activeHealthPartnerBranch.createdBy.lastname}`) ||
          d.activeHealthPartnerBranch.createdByText
        )
      },
    },
    {
      title: 'แก้ไขโดย',
      render: (_, data) => {
        const d = data as ActiveHealthPartnerBranchWithScanCount

        return (
          (d.activeHealthPartnerBranch.updatedBy.firstname &&
            d.activeHealthPartnerBranch.updatedBy.lastname &&
            `${d.activeHealthPartnerBranch.updatedBy.firstname} ${d.activeHealthPartnerBranch.updatedBy.lastname}`) ||
          d.activeHealthPartnerBranch.updatedByText
        )
      },
    },
    {
      title: 'แก้ไขล่าสุด',
      dataIndex: ['activeHealthPartnerBranch', 'updatedAt'],
      render: (updatedAt) => {
        return (updatedAt && dayjs(updatedAt).format('DD-MM-YYYY')) || '-'
      },
    },
    {
      title: 'ยอดสแกน',
      dataIndex: ['qrScanCount'],
      render: (qrScanCount: number) => {
        return qrScanCount.toLocaleString()
      },
    },
    {
      fixed: 'right',
      render: (_, data) => {
        const d = data as ActiveHealthPartnerBranchWithScanCount
        return (
          <ActionBlock key={d.activeHealthPartnerBranch.id}>
            <ButtonIcon src={IMAGE_URL.eyeIcon} onClick={() => openSidebarInformation(d)} />
            <ButtonIcon src={IMAGE_URL.qrIcon} onClick={() => onSaveQrCode(d)} />
            {isEditMode &&
              validatePerm([BACKOFFICE_USER_PERMISSION.WBE010104], user.permissions) && (
                <ButtonIcon
                  key={d.activeHealthPartnerBranch.id}
                  src={IMAGE_URL.editGreyIcon}
                  onClick={() => handleEditBranch(d)}
                />
              )}
            {isEditMode &&
              validatePerm([BACKOFFICE_USER_PERMISSION.WBE010107], user.permissions) &&
              d?.qrScanCount < 1 && (
                <ButtonIcon src={IMAGE_URL.deleteGreyIcon} onClick={() => onModalBranchDelete(d)} />
              )}
          </ActionBlock>
        )
      },
    },
  ]

  const onSaveQrCode = (data: ActiveHealthPartnerBranchWithScanCount) => {
    const filename = `qr-${
      data.activeHealthPartnerBranch.displayNameEn
    }-${new Date().toISOString()}.png`

    if (data.activeHealthPartnerBranch.qr.imageUrl) {
      saveAs(data.activeHealthPartnerBranch.qr.imageUrl, filename)
    } else {
      backofficeGetQrByActiveHealthPartnerBranchId({
        variables: {
          input: {
            partnerBranchId: data.activeHealthPartnerBranch.id,
          },
        },
      })
        .then((res) => {
          if (res.data?.backofficeGetQrByActiveHealthPartnerBranchId.imageUrl) {
            saveAs(res.data.backofficeGetQrByActiveHealthPartnerBranchId.imageUrl, filename)
          }
        })
        .catch((error) => {
          message.error({
            content: 'Error when downloading image!',
          })
        })
    }
  }

  const openSidebarInformation = (
    activeHealthPartnerBranchWithScanCount: ActiveHealthPartnerBranchWithScanCount,
  ) => {
    const subDistrictCode =
      activeHealthPartnerBranchWithScanCount.activeHealthPartnerBranch.subDistrict.subDistrictCode

    if (subDistrictCode) {
      getAddress({
        variables: {
          input: {
            subDistrictCode,
          },
        },
      }).then((address) => {
        if (address.data?.backofficeGetAddresses) {
          setSidebarInformation({
            visible: true,
            title: 'ข้อมูลสาขา',
            children: (
              <WellBeingPartnerSidebarInfo
                activeHealthPartnerBranchWithScanCount={activeHealthPartnerBranchWithScanCount}
                address={address.data.backofficeGetAddresses[0]}
              />
            ),
            dataTestId: 'well-being-partner-branch-info',
          })
        }
      })
    }
  }

  const getActiveHealthPartnerBranchWithScanCount = useCallback(
    (input?: BackofficeGetActiveHealthPartnerBranchPaginationWithScanCountInputInterface) => {
      backofficeGetActiveHealthPartnerBranchPaginationWithScanCount({
        variables: {
          activeHealthPartnerId: input?.activeHealthPartnerId || '',
          input: {
            page: pagination.page || 1,
            limit: pagination.limit || 10,
            sortBy: input?.input.sortBy,
            searchBy: ['displayNameEn', 'displayNameTh'],
            search: input?.input?.search || '',
          },
        },
      })
    },
    [backofficeGetActiveHealthPartnerBranchPaginationWithScanCount, pagination],
  )

  const onTablePaginate = (page: number, pageSize: number) => {
    setPagination({
      page,
      limit: pageSize,
    })
  }

  const onTableChange = (sorter: SorterResult<{}> | Array<SorterResult<{}>>) => {
    if (sorter) {
      const sort = sorter as SorterResult<{}>
      const sortBy = sort.columnKey as string
      const sortOrder = sort.order as SorterEnum

      setSort([sortBy, SORTER[sortOrder] || ''])
    }
  }

  const handleSetSearch = (input: string) =>
    of(setSearch(input)).pipe(
      tap(() => {
        setPagination({
          page: 1,
          limit: 10,
          totalItems: pagination.totalItems,
        })
      }),
    )

  const handleAddBranch = () => {
    setModalAction(WellBeingModalActionEnum.CREATE)
    setSelectedBranch(undefined)
    setIsModalOpen(true)
  }

  const handleEditBranch = (data: ActiveHealthPartnerBranchWithScanCount) => {
    setModalAction(WellBeingModalActionEnum.UPDATE)
    setSelectedBranch(data)
    setIsModalOpen(true)
  }

  const handleModalOk = () => {
    refetchPagination()
    setSelectedBranch(undefined)
    setIsModalOpen(false)
  }

  const handleModalCancel = () => {
    setSelectedBranch(undefined)
    setIsModalOpen(false)
  }

  useEffect(() => {
    if (partnerId) {
      getActiveHealthPartnerBranchWithScanCount({
        activeHealthPartnerId: partnerId,
        input: {
          search,
          page: pagination.page,
          limit: pagination.limit,
          sortBy: sort[0] ? [sort] : undefined,
        },
      })
    }
  }, [partnerId, search, pagination.page, pagination.limit, sort])

  useEffect(() => {
    if (isTyping && searchElement) {
      fromEvent(searchElement, 'keyup')
        .pipe(
          debounceTime(1000),
          map((e: Event) => {
            const target = e.target as HTMLInputElement

            return target.value || ''
          }),
          distinctUntilChanged(),
          switchMap(handleSetSearch),
          tap(() => setIsTyping(false)),
        )
        .subscribe()
    }
  }, [isTyping, searchElement])

  return {
    columns,
    activeHealthPartnerBranchData,
    activeHealthPartnerBranchLoading,
    pagination,
    isAddBranch,
    isModalOpen,
    modalAction,
    selectedBranch,
    onTablePaginate,
    setIsTyping,
    onTableChange,
    handleAddBranch,
    handleModalOk,
    handleModalCancel,
  }
}

export default WellBeingBranchTableHook
